import { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@material-ui/core';
import { Progress, WarningPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

export type ExistingRoute = { prefix: string; service: string; block: string };
type Routes = { host: string; routes: ExistingRoute[] };

const trim = (p: string) => p.replace(/\/$/, '');

/** Igual que el backend (istioRoute.ts): exacta = ya existe; solapada = una es prefijo de la otra. */
export const conflicts = (prefix: string, routes: ExistingRoute[]) => ({
  exact: prefix ? routes.find(r => trim(r.prefix) === trim(prefix)) : undefined,
  overlaps: prefix ? routes.filter(r => trim(r.prefix) !== trim(prefix) && (r.prefix.startsWith(prefix) || prefix.startsWith(r.prefix))) : [],
});

const rowStyle = (repeated: boolean, overlapping: boolean) => {
  if (repeated) return { background: 'rgba(244, 67, 54, 0.15)' };
  return overlapping ? { background: 'rgba(255, 152, 0, 0.15)' } : undefined;
};

// Lo que vio el campo, para que la validación del formulario no deje seguir con una ruta repetida.
const seen = new Map<string, ExistingRoute[]>();

/**
 * Campo de la ruta en "Pedir una ruta pública": muestra las rutas que ya existen en el dominio elegido
 * (de todos los bloques que lo comparten), filtradas por lo que se escribe, y marca la repetida y las que se solapan.
 */
export const RoutePrefixField = ({ formData, onChange, formContext, rawErrors, schema }: FieldExtensionComponentProps<string>) => {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const block = String((formContext?.formData as any)?.block ?? '');
  const [data, setData] = useState<Routes>();
  const [error, setError] = useState<string>();
  const prefix = formData ?? '';

  useEffect(() => {
    if (!block) return;
    setData(undefined);
    setError(undefined);
    (async () => {
      const base = await discovery.getBaseUrl('argo-values');
      const res = await fetch(`${base}/routes?block=${encodeURIComponent(block)}`);
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      seen.set(block, body.routes);
      setData(body);
    })().catch(e => setError(String(e?.message ?? e)));
  }, [block, discovery, fetch]);

  const { exact, overlaps } = conflicts(prefix, data?.routes ?? []);
  const shown = (data?.routes ?? []).filter(r => !prefix || r === exact || overlaps.includes(r) || r.prefix.includes(prefix));

  return (
    <div>
      <TextField
        fullWidth
        required
        label={schema.title ?? 'Ruta'}
        placeholder="/payments-api/graphql"
        value={prefix}
        error={!!exact || !!rawErrors?.length}
        helperText={exact ? `Ya existe: va a ${exact.service}` : rawErrors?.[0] ?? schema.description ?? 'Prefijo: todo lo que empiece por esta ruta va al servicio. Sin comodines.'}
        onChange={e => onChange(e.target.value.trim())}
      />
      {!block && <Typography color="textSecondary" variant="body2" style={{ marginTop: 8 }}>Elige el dominio para ver las rutas que ya tiene.</Typography>}
      {block && error && <WarningPanel title="No se pudieron leer las rutas del dominio" message={error} />}
      {block && !error && !data && <Progress />}
      {data && (
        <div style={{ marginTop: 12 }}>
          <Typography variant="body2" color="textSecondary">
            {data.routes.length} rutas en <b>{data.host}</b>
            {prefix ? ` · ${shown.length} coinciden con "${prefix}"` : ''}
            {overlaps.length > 0 && ' · en ámbar las que se solapan (Istio usa la primera que coincide): devops lo revisará.'}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ruta</TableCell>
                <TableCell>Servicio</TableCell>
                <TableCell>Bloque</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map(r => (
                <TableRow
                  key={`${r.block}${r.prefix}`}
                  style={rowStyle(r === exact, overlaps.includes(r))}
                >
                  <TableCell style={{ fontFamily: 'monospace' }}>{r.prefix}</TableCell>
                  <TableCell>{r.service}</TableCell>
                  <TableCell>{r.block.replace(/_public_traffic$/, '')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

/** No deja seguir si la ruta ya existe en el dominio (el backend lo vuelve a comprobar). */
export const validateRoutePrefix = (value: string | undefined, validation: { addError: (m: string) => void }, context: { formData: any }) => {
  const routes = seen.get(String(context?.formData?.block ?? '')) ?? [];
  const { exact } = conflicts(value ?? '', routes);
  if (exact) validation.addError(`${value} ya existe en este dominio: va a ${exact.service}`);
};
